import { useParams } from 'react-router-dom';

import {
  IndicatorFactType,
  PlotReportFactLabelFilter,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { useGetRadiusLabel } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { useHiddenFactConfigurationByFactTypes } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { assertIsDefined } from '@/utils/assert';

import { useGlobalPlotReport } from './usePlotReportForGlobalPlot';
import { usePlotReportBatchRequest, usePlotReportForPlot } from './usePlotReportForPlot';
import { formatFactLabelsForRequest, getFact, getFacts } from './utils';

/**
 * @param plotIdArg Falls back to the plotId in the URL if no value is provided.
 * @param factTypes List of fact types to be fetched.
 * @param labelFilters Fact labels to filter the facts (not sent to the API, filtered in the FE side).
 * @param radiusLabel Radius fact label to filter the facts.
 * @param sourceRef Fact source reference (e.g. in-situ tool file ID)
 * @param dateFilters Date filters to filter the geomodel server facts
 */
export const usePlotFacts = <T = unknown>(
  factTypes: IndicatorFactType[],
  plotIdArg?: string | null,
  labelFilters?: PlotReportFactLabelFilter,
  radiusLabel?: Record<PlotReportFactLabelNameEnum.landscape_radius_m, string>,
  sourceRef?: string,
  dateFilters?: {
    start_date_inclusive?: string;
    end_date_inclusive?: string;
  },
) => {
  const routeParams = useParams();
  const plotId = plotIdArg ?? routeParams.plotId;
  const hiddenFacts = useHiddenFactConfigurationByFactTypes(factTypes);

  assertIsDefined(plotId, 'plotId');

  const queryParams = {
    ...formatFactLabelsForRequest(labelFilters),
    fact_names: factTypes.filter((factType) => !hiddenFacts.includes(factType)).join(','),
    ...radiusLabel,
    ...(sourceRef ? { source_ref: sourceRef } : {}),
    ...(dateFilters?.start_date_inclusive ? { start_date_inclusive: dateFilters.start_date_inclusive } : {}),
    ...(dateFilters?.end_date_inclusive ? { end_date_inclusive: dateFilters.end_date_inclusive } : {}),
  };

  const plotReport = usePlotReportForPlot(plotId, queryParams).data;

  return getFacts<T>(plotReport, factTypes, labelFilters);
};

type FactWithFilters = {
  name: IndicatorFactType;
  dateFilters?: { start_date_inclusive?: string; end_date_inclusive?: string };
};

export const usePlotFactsBatchRequest = (facts: FactWithFilters[], plotIdArg?: string | null, sourceRef?: string) => {
  const routeParams = useParams();
  const plotId = plotIdArg ?? routeParams.plotId;
  const allFactTypes = facts.map((f) => f.name);
  const hiddenFacts = useHiddenFactConfigurationByFactTypes(allFactTypes);

  assertIsDefined(plotId, 'plotId');

  const filteredFacts = facts.filter((f) => !hiddenFacts.includes(f.name));

  const groups = [] as { factTypes: IndicatorFactType[]; start_date_inclusive?: string; end_date_inclusive?: string }[];
  groups.push({ factTypes: filteredFacts.filter((f) => !f.dateFilters).map((f) => f.name) });
  groups.push(
    ...filteredFacts
      .filter((f) => f.dateFilters)
      .map((f) => ({
        factTypes: [f.name],
        start_date_inclusive: f.dateFilters?.start_date_inclusive,
        end_date_inclusive: f.dateFilters?.end_date_inclusive,
      })),
  );

  const queryParamsArray = groups.map((group) => ({
    fact_names: group.factTypes.join(','),
    ...(sourceRef ? { source_ref: sourceRef } : {}),
    ...(group.start_date_inclusive ? { start_date_inclusive: group.start_date_inclusive } : {}),
    ...(group.end_date_inclusive ? { end_date_inclusive: group.end_date_inclusive } : {}),
  }));

  const results = usePlotReportBatchRequest(plotId, queryParamsArray);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return results.flatMap((result, i) => getFacts(result.data, groups[`${i}`]!.factTypes) ?? []);
};

export const useGlobalPlotFact = <T = unknown>(type: IndicatorFactType, labelFilters?: PlotReportFactLabelFilter) => {
  const plotReport = useGlobalPlotReport().data;

  return getFact<T>(plotReport, type, labelFilters, false);
};

export const usePlotFactsByRadius = <T = unknown>(types: IndicatorFactType[]) => {
  const routeParams = useParams();

  const radiusLabel = useGetRadiusLabel();

  return usePlotFacts<T>(types, routeParams.plotId || null, undefined, radiusLabel);
};
